<script setup lang="ts" generic="T extends SelectInputType">
import { computed, ref } from 'vue';

import type {
  IFormPlainDataListboxPropsChangeState,
  SelectInputType,
} from '@modules/forms/types';
import VFormPlainDataListbox from '@modules/forms/components/inputs/VFormPlainDataListbox/VFormPlainDataListbox.vue';
import VFormTextInput from '@modules/forms/components/inputs/VFormTextInput/VFormTextInput.vue';
import { TRANSLATION_GROUP_FORMS_COMMON } from '@modules/forms/constants';
import { useI18n } from '@modules/i18n/composables';

import type {
  VFormMultiselectFilterWidgetEmits,
  VFormMultiselectFilterWidgetProps,
} from './types';

const props = withDefaults(defineProps<VFormMultiselectFilterWidgetProps<T>>(), {});
const emit = defineEmits<VFormMultiselectFilterWidgetEmits<T>>();

const { getTranslation } = useI18n();

const inputNameTouched = ref(false);

const isInputNameInvalid = computed<boolean>(() => props.requiredName && !props.filterValue && inputNameTouched.value);
const inputNameErrors = computed<string[] | undefined>(() => (
  isInputNameInvalid.value && inputNameTouched.value
    ? [getTranslation(TRANSLATION_GROUP_FORMS_COMMON, 'ERROR_REQUIRED').value]
    : undefined
));

function onChange(change: IFormPlainDataListboxPropsChangeState<T>): void {
  emit('change-value', change.values.map((item) => item.id));
}
</script>

<template>
  <VFormPlainDataListbox v-data-qa-auto-id="'VFormMultiselectFilterWidget'"
    :data="options"
    :errors="errors"
    :readonly="readonly"
    @change="onChange"
  >
    <template #footer>
      <VFormTextInput
        :label="filterLabel"
        :value="filterValue"
        :readonly="readonly"
        :required="requiredName"
        :hide-errors="!requiredName"
        :is-invalid="isInputNameInvalid"
        :errors="inputNameErrors"
        @blur="inputNameTouched = true"
        @change="emit('change-name', $event)"
        @input="emit('change-name', $event)"
      />
    </template>
  </VFormPlainDataListbox>
</template>
